import React, { FC } from 'react';
import { Image, Text, View } from '@react-pdf/renderer';
import { BadgeText } from './text/BadgeText';

import { BadgeCustomerType } from './footer/BadgeCustomerType';
import utils from './utils';
import { BadgeQrCode } from './qrCode/BadgeQrCode';
import { BadgeIcons } from './icons/BadgeIcons';
import { BadgeJobTitle } from './jobTitle/BadgeJobTitle';
import { BadgeName } from './name/BadgeName';
import { BadgeCountryName } from './countryName/BadgeCountryName';
import { BadgeElementViewOptions, PaperSize, PDFBadgeInfo } from '../../../types/badge';
import { DownloadFileTypes, FONT, PDFEntitlementIcon } from '../../../types/enums';
import PDFUtils from '../../utils/pdfUtils';
import { getFontFamily, getPdfStyles, updatePdfStyles } from '../utils';
import { JapaneseBadgeElement } from '../japaneseBadgeElement/JapaneseBadgeElement';
import { Style } from '@react-pdf/types';
import EventEditionHelper from '../../../common/helpers/eventEdition/EventEditionHelper';
import { Nullable } from "../../../types/common";

type Props = PaperSize & {
  badgeInfo: PDFBadgeInfo;
  badgeSuffix?:Nullable<string>;
  imageSrc: string;
  qrCodeUrl?: string;
  additionalStyles?: Style;
  badgeElementViewOptions: BadgeElementViewOptions;
  badgeIcons: Array<PDFEntitlementIcon>;
  isCustomBadge: boolean;
  type: DownloadFileTypes;
  rotateAngle?: number;
  shouldDownscaleBadge?: boolean;
};

const BadgeElement: FC<Props> = ({
  rotateAngle = 0,
  isCustomBadge,
  badgeInfo: {
    badgeId,
    firstName: badgeFirstName,
    lastName: badgeLastName,
    jobTitle: initialJobTitle,
    companyDisplayName,
    standIds,
    countryCode,
    city,
    stateCode,
    badgeVersion
  },
  badgeSuffix,
  paperSize,
  imageSrc,
  qrCodeUrl,
  additionalStyles = {},
  badgeElementViewOptions,
  badgeIcons,
  type,
  shouldDownscaleBadge
}) => {
  const isJapanese = EventEditionHelper.getIsJapaneseBusinessUnitCode();
  const badgeSuffixDesc = badgeSuffix ? `, ${badgeSuffix}` : '';
  // We are adding +2 to account for the comma and space before the suffix
  const badgeSuffixLength = badgeSuffix?.length ? badgeSuffix.length + 2 : 0;
  const displayName = isJapanese ? `${badgeLastName} ${badgeFirstName}` : `${badgeFirstName} ${badgeLastName}`;
  const splittedName = utils.removeMultipleSpacesFromString(displayName).split(' ');
  const splittedLastName = displayName.substr(displayName.indexOf(' ') + 1);
  const isSingleLineName = splittedName[0].length + splittedLastName.length <= 16;
  const truncatedFirstName = utils.getTruncatedString(isSingleLineName ? displayName : badgeFirstName, badgeSuffixLength);
  const truncatedLastName = utils.getTruncatedString(badgeLastName, badgeSuffixLength);
  const firstName = isSingleLineName ? truncatedFirstName.concat(badgeSuffixDesc) : truncatedFirstName.concat(isJapanese ? badgeSuffixDesc : '');
  const lastName = isJapanese ? truncatedLastName : truncatedLastName.concat(badgeSuffixDesc);
  const jobTitle = utils.getTruncatedString(initialJobTitle);
  const companyNameTruncated = utils.getTruncatedString(
    `${companyDisplayName}${standIds.length && standIds[0].length > 0 ? utils.getIdInBrakets(companyDisplayName, standIds[0]) : ''}`
  );
  const address = utils.getDisplayCountryName(
    countryCode,
    utils.getTruncatedString(city || ''),
    utils.getTruncatedString(stateCode || '')
  );
  const logoSrc = PDFUtils.returnSrc(imageSrc);

  const pdfStyles = updatePdfStyles(getPdfStyles(paperSize), paperSize, isSingleLineName, jobTitle.length);

  const nameFontSize = {
    fontSize: utils.getAdjustedFontSize({
      str1: firstName,
      str2: lastName,
      paperSize,
      initialFontSize: 24,
      additionalMaxWidthLength: 30,
      styles: pdfStyles.name
    })
  };

  const jobTitleCompanyNameFontSize = utils.getAdjustedFontSize({
    str1: jobTitle,
    str2: companyNameTruncated,
    paperSize,
    initialFontSize: 18
  });

  const addressFontSize = utils.getAdjustedFontSize({
    str1: companyNameTruncated,
    str2: address,
    paperSize,
    initialFontSize: pdfStyles.address.fontSize as number
  });

  const qrCodeMarginTop = jobTitleCompanyNameFontSize > 10 ? 35 : 25;
  const containerStyles: Array<Style> = [pdfStyles.badgeItem, additionalStyles, { position: 'relative' }];

  const getFooter4x10InchesStyles = () => {
    if (isCustomBadge) {
      return pdfStyles.customerType4x10Inches;
    }
    return {};
  };

  const getFooterWrapperStyles = () => {
    return getFooter4x10InchesStyles();
  };

  const isLogoHidden = () => {
    return false;
    // return type === DownloadFileTypes.ZPL || paperSize === PAPER_SIZE.W105xH140mm;
  };

  const getDisplayBadgeId = () => {
    if (badgeVersion) {
      const displayBadgeVersion = badgeVersion < 10 ? `0${badgeVersion}` : badgeVersion;
      return `${badgeId}-${displayBadgeVersion}`;
    }
    return badgeId;
  };

  if (isJapanese) {
    return (
      <JapaneseBadgeElement
        rotateAngle={rotateAngle}
        type={type}
        badgeIcons={badgeIcons}
        personData={{
          isSingleLineName,
          lastName,
          firstName,
          jobTitle
        }}
        logo={logoSrc}
        qrCodeUrl={qrCodeUrl}
        isCustomBadge={isCustomBadge}
        paperSize={paperSize}
        badgeId={getDisplayBadgeId()}
        additionalStyles={{
          ...additionalStyles,
          ...{
            paddingTop: isCustomBadge ? 0 : 6
          }
        }}
        companyNameTruncated={companyNameTruncated}
      />
    );
  }
  const badgeRotationStyle = rotateAngle
    ? {
        transform: `rotate(${rotateAngle}deg)`
      }
    : {};

  const downscaleStyle = shouldDownscaleBadge ? pdfStyles.downscaleTo80Percents : {};
  return (
    <View
      data-testid="pdf-main-view"
      style={[
        ...containerStyles,
        pdfStyles.relative,
        downscaleStyle,
        isCustomBadge ? pdfStyles.badgeItemFullWidth : {},
        badgeRotationStyle
      ]}
    >
      {isLogoHidden() ? (
        <View data-testid="badge-logo-filler" style={pdfStyles.eventLogo} />
      ) : (
        <Image data-testid="badge-logo-image" source={logoSrc} style={pdfStyles.eventLogo} />
      )}

      <View style={[pdfStyles.nameView]}>
        <Text style={[pdfStyles.name, nameFontSize, getFontFamily(firstName, { fallbackFont: FONT.HELVETICA_BOLD })]}>{firstName}</Text>
        {!isSingleLineName && <BadgeName name={lastName} fontSize={nameFontSize} />}
      </View>

      <BadgeJobTitle jobTitle={jobTitle} isSingleLineName={isSingleLineName} fontSize={jobTitleCompanyNameFontSize} />

      <BadgeText
        text={companyNameTruncated}
        style={[
          pdfStyles.company,
          { fontSize: jobTitleCompanyNameFontSize },
          getFontFamily(companyNameTruncated, { fallbackFont: FONT.HELVETICA })
        ]}
      />

      <BadgeCountryName styles={pdfStyles} address={address} addressFontSize={addressFontSize} />

      <BadgeQrCode
        isCustomBadge={isCustomBadge}
        qrCodeUrl={qrCodeUrl}
        isSingleLineName={isSingleLineName}
        paperSize={paperSize}
        qrCodeMarginTop={qrCodeMarginTop}
      />
      <Text style={pdfStyles.id}>{getDisplayBadgeId()}</Text>

      <BadgeIcons paperSize={paperSize} pdfStyles={pdfStyles} icons={badgeIcons} isCustomBadge={isCustomBadge} />
      <BadgeCustomerType
        additionalWrapperStyles={{ ...getFooterWrapperStyles() }}
        badgeElementViewOptions={badgeElementViewOptions}
        paperSize={paperSize}
      />
    </View>
  );
};

export default BadgeElement;
